import React, {useEffect, useRef, useState} from 'react';
import {IonAlert, IonCol, IonIcon, IonItem, IonLabel, IonLoading, IonRow, IonSelectOption, IonToast} from '@ionic/react';
import {useTranslation} from 'react-i18next';
import {Controller, useFormContext, useWatch} from "react-hook-form";
import moment, {Moment} from "moment";
import {PlaceOfService, RideRoute} from "@models/travelAllowance/rideRoute";
import {Car} from "@models/travelAllowance/car";
import {Coordinator, Passenger, PassengerFormField, RideFormData} from "@models/travelAllowance/ride";
import {RideCost} from "@models/travelAllowance/rideCost";
import {RideMatrix} from "@models/travelAllowance/rideMatrix";
import Accordion from "@components/accordion/accordion";
import DateInput from '@components/form/input/date.component';
import PassengersFormField from "@app/travelAllowance/ride/components/passengersFormField.component";
import CityAutocomplete from "@components/google/cityAutocomplete/cityAutocomplete.component";
import PaginatedListModal from "@components/modal/paginatedListModal.component";
import CalendarIcon from '@assets/images/travelAllowance/calendar.svg';
import {getPlacesOfService, getPossibleCoordinators, validateRideRoute} from "@services/travelAllowance/rideRoute.service";
import {calculateRideCost} from "@services/travelAllowance/rideCost.service";
import {handleCommonRideErrorResponseMessage} from "@services/travelAllowance/ride.service";
import {useRideFormContext} from "@context/rideForm.context";
import {RidePaneType} from "@enums/travelAllowance/ride";
import useComponentMounted from "@hooks/useComponentMounted";
import {StyledInput, StyledInputGroup, StyledInputUnit} from "@components/form/input/input.style";
import {StyledIonLabel} from "@components/form/input/label.style";
import {StyledIonCheckbox} from "@components/form/input/checkbox.style";
import {StyledIonSelect} from "@components/form/input/select.style";
import RideCostInfoRow from "@app/travelAllowance/ride/components/rideCostInfoRow.component";
import {getRideMatrix} from "@services/travelAllowance/rideMatrix.service";
import SubRoutesFormField, {SubRoutesFormFieldHandle} from "@app/travelAllowance/rideRoute/components/subRoutesFormField.component";
import {updateSubRouteHiddenFields, validateSubRoutes} from '@services/travelAllowance/subRoute.service';
import {getFieldNameWithPrefix} from "@services/travelAllowance/travelAllowance";
import {getDistance} from "@services/google/googleMaps.service";
import {Preferences} from "@capacitor/preferences";
import {Coordinates} from "@models/google/googleMaps";
import {getServerNow} from "@services/serverDate.service";

const RideFormFields: React.FC = () => {
    const {t} = useTranslation();
    const {register, control, setValue, errors, getValues} = useFormContext<RideFormData>();

    const isMounted = useComponentMounted();
    const rideFormContext = useRideFormContext();

    const [shouldFetchRideCost, setShouldFetchRideCost] = useState<boolean>(false);

    const [selectedPassengers, setSelectedPassengers] = useState<Passenger[]>();
    const [selectedRoute, setSelectedRoute] = useState<RideRoute>();
    const [dateStart, setDateStart] = useState<string>();
    const [rideCost, setRideCost] = useState<RideCost>();
    const [coordinator, setCoordinator] = useState<Coordinator | undefined>();
    const [placeOfService, setPlaceOfService] = useState<PlaceOfService | undefined>();
    const [distanceValue, setDistanceValue] = useState<number>();
    const [rideMatrix, setRideMatrix] = useState<RideMatrix>();
    const [minDateStart, setMinDateStart] = useState<string>();
    const [defaultPassengers, setDefaultPassengers] = useState<PassengerFormField[]>([]);
    const [workerCoordinatorId, setWorkerCoordinatorId] = useState<number | null>(null)

    const [startingPlaceCoordinates, setStartingPlaceCoordinates] = useState<Coordinates | undefined>();
    const [destinationPlaceCoordinates, setDestinationPlaceCoordinates] = useState<Coordinates | undefined>();

    const [isCoordinatorModalOpen, setIsCoordinatorModalOpen] = useState<boolean>(false);
    const [isPlaceOfServiceModalOpen, setIsPlaceOfServiceModalOpen] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toast, setToast] = useState<string>('');
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showDistanceErrorAlert, setShowDistanceErrorAlert] = useState<boolean>(false);

    const subRoutesRef = useRef<SubRoutesFormFieldHandle>(null);

    const startingPlace = useWatch({control: control, name: 'ride.route.startingPlace'});
    const destinationPlace = useWatch({control: control, name: 'ride.route.destinationPlace'});
    const distance = useWatch({control: control, name: 'ride.route.distance'});

    useEffect(() => {
        setRideMatrix(rideFormContext.rideMatrix);
        handleMinDateStart();
        setDefaultValues();
        handleWorkerCoordinatorId();
    }, []);

    useEffect(() => {
        if (distance) {
            setDistanceValue(distance);
        }
    }, [distance]);

    useEffect(() => {
        setShowLoader(true);

        fetchRideMatrix()
            .then((rideMatrix: RideMatrix) => {
                setRideMatrix(rideMatrix);
            })
            .finally(() => setShowLoader(false));
    }, []);

    useEffect(() => {
        if (!startingPlace || !destinationPlace) {
            setValue('ride.route.distance', undefined);
            setDistanceValue(undefined);
        }
    }, [startingPlace, destinationPlace]);

    useEffect(() => {
        if (!isMounted) return;
        if (!shouldFetchRideCost) return;

        const {ride: {route, car}} = control.getValues();
        if (!route.startingPlaceCountry || !route.destinationPlaceCountry || !distanceValue || !car || !dateStart) {
            setShouldFetchRideCost(false);
            return;
        }

        setShowLoader(true);

        const handleError = (error: any) => {
            const errorMessage = error.response.data.detail;
            const messageKey = handleCommonRideErrorResponseMessage(errorMessage, 'travelAllowance.rideCost.calculateError');

            if (errorMessage === 'Company vehicle rate not found') {
                setValue('ride.car', undefined);
            }

            setToast(t(messageKey));
            setShowToast(true);
            setRideCost(undefined);

            console.error(error);
        };

        fetchRideCost(route.startingPlaceCountry, route.destinationPlaceCountry, distanceValue, car, dateStart, selectedPassengers)
            .catch((error) => handleError(error))
            .finally(() => {
                setShouldFetchRideCost(false);
                setShowLoader(false);
            })
    }, [isMounted, shouldFetchRideCost]);

    useEffect(() => {
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const fetchDistance = async () => {
        const {ride: {route}} = control.getValues();

        if (!route.startingPlace || !route.destinationPlace) return;

        if (route.subRoutes) {
            if (!validateSubRoutes(route.subRoutes)) return;
        }

        const handleError = (error: any, errorMessageKey: string) => {
            const errorMessage = error.response.data.detail;

            if (errorMessage === 'Could not determine distance.') {
                setShowDistanceErrorAlert(true);
            } else {
                setToast(t(errorMessageKey));
                setShowToast(true);
            }

            console.error(error);
        };

        setShowLoader(true);

        let waypoints: string[] | undefined;
        if (route.subRoutes && route.subRoutes.length > 1) {
            waypoints = route.subRoutes?.slice(1).map(subRoute => subRoute.startingPlace);
        }

        getDistance(route.startingPlace, route.destinationPlace, waypoints)
            .then((data) => {
                setValue('ride.route.distance', data.distance.total, {shouldValidate: true});
                setDistanceValue(data.distance.total);

                if (waypoints) {
                    data.distance.waypoints.forEach((waypoint, index) => {
                        setValue(getFieldNameWithPrefix(`subRoutes.${index}.distance`, 'ride.route'), waypoint.distance);
                    });
                }

                setShouldFetchRideCost(true);
            })
            .catch((error) => {
                handleError(error, "travelAllowance.rideRoute.distanceCalculateError")
            })
            .finally(() => {
                setShowLoader(false);
            })
    }

    const fetchRideMatrix = async (date?: string) => {
        return await getRideMatrix(date);
    }

    const fetchRideCost = async (startingPlaceCountry: string, destinationPlaceCountry: string, distance: number, car: number, dateStart: string, passengers?: Passenger[]) => {
        const rideCostData = await calculateRideCost({
            startingPlaceCountry,
            destinationPlaceCountry,
            distance,
            dateStart,
            car,
            passengers: passengers ? passengers.map(passenger => passenger.id) : [],
        });

        setRideCost(rideCostData);
    }

    const handleWorkerCoordinatorId = async () => {
        const coordinatorId = await Preferences.get({'key': 'coordinator_id'});

        if (coordinatorId.value) {
            setWorkerCoordinatorId(parseInt(coordinatorId.value));
        }
    }

    const handleDateChange = async (dateInput: string, onChangeCallback: (...event: any[]) => void) => {
        setShowLoader(true);

        if (moment(dateInput).isAfter(getServerNow())) {
            setToast(t("travelAllowance.ride.incorrectDate"));
            setShowToast(true);
            setShowLoader(false);
            return;
        }

        dateInput = moment(dateInput).format('YYYY-MM-DD')

        let validationSuccess = true;
        if (selectedRoute) {
            await validateRideRoute(selectedRoute.id, dateInput)
                .catch((error) => {
                    handleRideRouteValidationError(error);
                    validationSuccess = false;
                })
        }

        if (!validationSuccess) {
            setShowLoader(false);
            return;
        }

        fetchRideMatrix(dateInput)
            .then((rideMatrix: RideMatrix) => {
                if (Object.keys(rideMatrix).length > 0) {
                    setDateStart(dateInput);
                    setRideMatrix(rideMatrix)

                    if (!selectedRoute && workerCoordinatorId !== coordinator?.id) {
                        handleCoordinatorClear();
                        handlePlaceOfServiceClear();
                    }

                    onChangeCallback(dateInput);
                    setShouldFetchRideCost(true);
                } else {
                    setToast(t("travelAllowance.rideMatrix.notFound"));
                    setShowToast(true);
                }
            })
            .finally(() => setShowLoader(false));
    }

    const handleRouteSelect = async (routeId: number | null) => {
        setShowLoader(true);

        let validationSuccess = true;
        if (routeId) {
            await validateRideRoute(routeId, control.getValues().ride?.dateStart)
                .catch((error) => {
                    handleRideRouteValidationError(error);
                    validationSuccess = false;
                })
                .finally(() => {
                    setShowLoader(false);
                })
        }

        if (!validationSuccess) {
            return;
        }

        const rideRoute = rideFormContext.rideRoutes.find((rideRoue) => rideRoue.id === routeId);
        setSelectedRoute(rideRoute);

        setValue('ride.route.id', rideRoute?.id ?? 0);
        setValue('ride.route.startingPlace', rideRoute?.startingPlace ?? '', {shouldValidate: true});
        setValue('ride.route.destinationPlace', rideRoute?.destinationPlace ?? '', {shouldValidate: true});
        setValue('ride.route.startingPlaceCountry', rideRoute?.startingPlaceCountry ?? '');
        setValue('ride.route.destinationPlaceCountry', rideRoute?.destinationPlaceCountry ?? '');
        setValue('ride.route.distance', rideRoute?.distance ?? undefined);
        setValue('ride.route.coordinator', rideRoute?.coordinator?.id ?? undefined, {shouldValidate: true});
        setValue('ride.route.placeOfService', rideRoute?.placeOfService?.id ?? undefined, {shouldValidate: true});
        setValue('ride.route.isLocal', rideRoute?.isLocal ?? undefined);
        setValue('ride.route.subRoutes', undefined);

        if (rideRoute?.subRoutes) {
            subRoutesRef.current?.setRows(rideRoute.subRoutes);
        } else {
            subRoutesRef.current?.removeRows();
        }

        setDistanceValue(rideRoute?.distance);
        setCoordinator(rideRoute?.coordinator);
        setPlaceOfService(rideRoute?.placeOfService);

        rideFormContext.setCoordinator(rideRoute?.coordinator);
        rideFormContext.setPlaceOfService(rideRoute?.placeOfService);

        if (!rideRoute) {
            setRideCost(undefined);
        } else {
            setShouldFetchRideCost(true);
        }

        setShowLoader(false);
    }

    const handleRideRouteValidationError = (error: any) => {
        const errorMessage = error.response.data.detail;

        if (errorMessage.includes('Data integrity failed for coordinatorId')) {
            setToast(t("travelAllowance.rideRoute.coordinatorIntegrityViolation"));
        }

        if (errorMessage.includes('Data integrity failed for placeOfServiceId')) {
            setToast(t("travelAllowance.rideRoute.placeOfServiceIntegrityViolation"));
        }
        setShowToast(true);

        console.error(error);
    };

    const setStartingPlaceCountryCallback = (country: string) => {
        setValue('ride.route.startingPlaceCountry', country);
    }

    const setDestinationPlaceCountryCallback = (country: string) => {
        setValue('ride.route.destinationPlaceCountry', country);
    }

    const handleStartingCoordinatesSet = (coordinates?: Coordinates) => {
        setValue('ride.route.startingLatitude', coordinates?.latitude);
        setValue('ride.route.startingLongitude', coordinates?.longitude);

        setStartingPlaceCoordinates(coordinates);
    }

    const handleDestinationCoordinatesSet = (coordinates?: Coordinates) => {
        setValue('ride.route.destinationLatitude', coordinates?.latitude);
        setValue('ride.route.destinationLongitude', coordinates?.longitude);

        setDestinationPlaceCoordinates(coordinates);
    }

    const setDefaultValues = () => {
        if (rideFormContext.cars.length === 1) {
            setValue('ride.car', rideFormContext.cars[0].id);
        }

        if (rideFormContext.rideDefaultValues) {
            const {passengers, rideCost} = rideFormContext.rideDefaultValues;

            const tempPassengers: PassengerFormField[] = passengers.map((passenger: Passenger): PassengerFormField => {
                return {
                    workerId: passenger.id,
                    name: passenger.name,
                }
            })

            setDateStart(rideFormContext.rideDefaultValues.dateStart);
            setRideCost(rideCost);
            setDistanceValue(rideFormContext.rideDefaultValues.rideRoute.distance);
            setDefaultPassengers(tempPassengers);

            handleCoordinatorSelect(rideFormContext.rideDefaultValues.rideRoute.coordinator);
            handlePlaceOfServiceSelect(rideFormContext.rideDefaultValues.rideRoute.placeOfService);

            const startingPlaceCoordinatesDefaultValues =
                rideFormContext.rideDefaultValues.rideRoute.startingLatitude && rideFormContext.rideDefaultValues.rideRoute.startingLongitude
                    ? {latitude: rideFormContext.rideDefaultValues.rideRoute.startingLatitude, longitude: rideFormContext.rideDefaultValues.rideRoute.startingLongitude}
                    : undefined;

            const destinationPlaceCoordinatesDefaultValues =
                rideFormContext.rideDefaultValues.rideRoute.destinationLatitude && rideFormContext.rideDefaultValues.rideRoute.destinationLongitude
                    ? {latitude: rideFormContext.rideDefaultValues.rideRoute.destinationLatitude, longitude: rideFormContext.rideDefaultValues.rideRoute.destinationLongitude}
                    : undefined;

            handleStartingCoordinatesSet(startingPlaceCoordinatesDefaultValues);
            handleDestinationCoordinatesSet(destinationPlaceCoordinatesDefaultValues);
        } else {
            setDateStart(getServerNow().format());
        }
    }

    const handleCoordinatorSelect = (coordinator: Coordinator) => {
        setCoordinator(coordinator)
        setValue('ride.route.coordinator', coordinator.id, {shouldValidate: true});
        rideFormContext.setCoordinator(coordinator);

        if (coordinator.id !== workerCoordinatorId) {
            handlePlaceOfServiceClear();
        }
    }

    const handlePlaceOfServiceSelect = (placeOfService: PlaceOfService) => {
        setPlaceOfService(placeOfService);
        setValue('ride.route.placeOfService', placeOfService.id, {shouldValidate: true});
        rideFormContext.setPlaceOfService(placeOfService);
    }

    const handleCoordinatorClear = () => {
        setCoordinator(undefined)
        setValue('ride.route.coordinator', undefined, {shouldValidate: true});
        rideFormContext.setCoordinator(undefined);
    }

    const handlePlaceOfServiceClear = () => {
        setPlaceOfService(undefined);
        setValue('ride.route.placeOfService', undefined, {shouldValidate: true});
        rideFormContext.setPlaceOfService(undefined);
    }

    const handleDistanceErrorAlertConfirm = () => {
        setValue('ride.route.startingPlace', '');
        setValue('ride.route.destinationPlace', '');
        setValue('ride.route.startingPlaceCountry', '');
        setValue('ride.route.destinationPlaceCountry', '');
        setValue('ride.route.distance', undefined);
        setValue('ride.route.isLocal', undefined);
        setValue('ride.route.startingLatitude', undefined);
        setValue('ride.route.startingLongitude', undefined);
        setValue('ride.route.destinationLatitude', undefined);
        setValue('ride.route.destinationLongitude', undefined);
    }

    const handleMinDateStart = () => {
        const maxDaysBack = rideFormContext.workerFormFormSettings?.travel_allowance_max_days_back === undefined ? null : rideFormContext.workerFormFormSettings?.travel_allowance_max_days_back;
        const nextPeriodDays = rideFormContext.workerFormFormSettings?.travel_allowance_max_days_next_period === undefined ? null : rideFormContext.workerFormFormSettings?.travel_allowance_max_days_next_period;

        const minDate = getMinDate(nextPeriodDays, maxDaysBack).format();

        setMinDateStart(minDate);
    };

    function getMinDate(maxDaysNextPeriodForRide: number | null, maxDaysBackForRide: number | null): Moment {
        const today = getServerNow();
        const firstDayOfMonth = today.clone().startOf("month");
        const firstDayOfLastMonth = today.clone().subtract(1, "months").startOf("month");
        const daysInCurrentMonth = today.date();

        if (maxDaysNextPeriodForRide === null) {
            if (maxDaysBackForRide === null) {
                return firstDayOfLastMonth;
            }

            return today.clone().subtract(maxDaysBackForRide, "days");
        }

        if (maxDaysNextPeriodForRide === 0) {
            if (maxDaysBackForRide === null) {
                return firstDayOfMonth;
            }

            return daysInCurrentMonth > maxDaysBackForRide ? today.clone().subtract(maxDaysBackForRide, "days") : firstDayOfMonth;
        }

        if (maxDaysNextPeriodForRide > 0) {
            if (maxDaysBackForRide === null) {
                return maxDaysNextPeriodForRide >= daysInCurrentMonth ? firstDayOfLastMonth : firstDayOfMonth;
            }

            if (maxDaysNextPeriodForRide >= daysInCurrentMonth || maxDaysBackForRide <= maxDaysNextPeriodForRide) {
                return today.clone().subtract(maxDaysBackForRide, "days");
            }

            if (maxDaysBackForRide > maxDaysNextPeriodForRide) {
                return firstDayOfMonth;
            }
        }

        return firstDayOfLastMonth;
    }

    const timerRef = useRef<number | null>(null);

    function handleDistanceChanged(value: string) {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = window.setTimeout(() => {
            setDistanceValue(parseInt(value));
            setShouldFetchRideCost(true);
        }, 1000);
    }

    const updateHiddenFields = () => {
        const {ride: {route}} = control.getValues();

        if (route.subRoutes) {
            updateSubRouteHiddenFields(route.subRoutes, setValue, getValues, 'ride.route');
        }
    }

    return (
        <>
            <Accordion isOpen={true} allowToggle={true} title={t('travelAllowance.ride.generalData')}>
                <IonLoading onDidDismiss={() => setShowLoader(false)} isOpen={showLoader}/>
                <IonAlert
                    isOpen={showDistanceErrorAlert}
                    onDidDismiss={() => setShowDistanceErrorAlert(false)}
                    header={t('travelAllowance.rideRoute.distanceErrorAlert')}
                    buttons={[
                        {
                            text: t('common.ok'),
                            handler: () => {
                                handleDistanceErrorAlertConfirm();
                            }
                        }
                    ]}
                />
                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={toast}
                    duration={6000}
                    position="top"
                    color="danger"
                />
                <PaginatedListModal isOpen={isCoordinatorModalOpen}
                                    fetchData={getPossibleCoordinators}
                                    fetchParams={{date: control.getValues().ride?.dateStart, cancelToken: undefined}}
                                    setValue={handleCoordinatorSelect}
                                    onClose={() => setIsCoordinatorModalOpen(false)}
                />
                <PaginatedListModal isOpen={isPlaceOfServiceModalOpen}
                                    fetchData={getPlacesOfService}
                                    fetchParams={{
                                        coordinatorId: coordinator?.id,
                                        startingPlaceCoordinates: startingPlaceCoordinates,
                                        destinationPlaceCoordinates: destinationPlaceCoordinates,
                                        localizationDistanceLimit: rideMatrix?.localizationDistanceLimit,
                                        cancelToken: undefined
                                    }}
                                    fallbackFetchParams={
                                        startingPlaceCoordinates && destinationPlaceCoordinates && rideMatrix?.localizationDistanceLimit && rideMatrix.localizationDistanceLimit > 0
                                            ? {
                                                coordinatorId: coordinator?.id,
                                                startingPlaceCoordinates: undefined,
                                                destinationPlaceCoordinates: undefined,
                                                localizationDistanceLimit: undefined,
                                                cancelToken: undefined
                                            }
                                            : undefined
                                    }
                                    fallbackFetchButtonTitle={
                                        startingPlaceCoordinates && destinationPlaceCoordinates && rideMatrix?.localizationDistanceLimit && rideMatrix.localizationDistanceLimit > 0
                                            ? t('travel_allowance.ride.placeOfService.loadRest')
                                            : undefined
                                    }
                                    setValue={handlePlaceOfServiceSelect}
                                    onClose={() => setIsPlaceOfServiceModalOpen(false)}
                />
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.startDate')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <StyledInputGroup className={errors.ride?.dateStart ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('dateStart') ? 'warning' : '')}>
                    <IonCol size="10" className="label">
                        <Controller
                            name={'ride.dateStart'}
                            control={control}
                            defaultValue={dateStart ?? control.getValues().ride?.dateStart}
                            rules={{required: true}}
                            render={({onChange}) => (
                                <DateInput
                                    presentation="date"
                                    format="DD.MM.YYYY"
                                    onChange={value => {
                                        handleDateChange(value, onChange);
                                    }}
                                    min={minDateStart}
                                    max={getServerNow().format()}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("common.select")}
                                    value={moment(dateStart ?? control.getValues().ride?.dateStart).format('YYYY-MM-DD')}
                                />
                            )}
                        />
                    </IonCol>
                    <IonCol size="2" className="label">
                        <StyledInputUnit icon>
                            <IonIcon slot="icon-only" icon={CalendarIcon}/>
                        </StyledInputUnit>
                    </IonCol>
                </StyledInputGroup>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.rideRoute')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller
                            name={'ride.route.id'}
                            defaultValue={control.getValues()?.ride?.route.id || undefined}
                            control={control}
                            render={() => (
                                <StyledIonSelect
                                    placeholder={t('travelAllowance.ride.selectRoute')}
                                    onIonChange={(e) => handleRouteSelect(e.detail.value)}
                                    value={control.getValues()?.ride?.route.id || undefined}
                                >
                                    <IonSelectOption value={0}>{t('travelAllowance.ride.selectRoute')}</IonSelectOption>
                                    {rideFormContext.rideRoutes.map((rideRoute: RideRoute) => {
                                        return <IonSelectOption
                                            key={rideRoute.id}
                                            value={rideRoute.id}>{rideRoute.startingPlace} - {rideRoute.destinationPlace}</IonSelectOption>
                                    })}
                                </StyledIonSelect>
                            )}
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.startingPlace')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller
                            name={'ride.route.startingPlace'}
                            rules={{required: true}}
                            control={control}
                            defaultValue={''}
                            render={({onChange}) => (
                                <CityAutocomplete disabled={!!selectedRoute}
                                                  defaultValue={startingPlace}
                                                  setCountry={setStartingPlaceCountryCallback}
                                                  setCoordinates={handleStartingCoordinatesSet}
                                                  onChangeCallback={async (place: any) => {
                                                      onChange(place);
                                                      updateHiddenFields();
                                                      await fetchDistance();
                                                  }}
                                                  className={errors.ride?.route?.startingPlace ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('rideRoute.startingPlace') ? 'warning' : '')}
                                />
                            )}
                        />
                        <input ref={register}
                               name={'ride.route.startingPlaceCountry'}
                               type={"hidden"}
                        />
                        <input ref={register}
                               name={'ride.route.startingLatitude'}
                               type={"hidden"}
                        />
                        <input ref={register}
                               name={'ride.route.startingLongitude'}
                               type={"hidden"}
                        />
                    </IonCol>
                </IonRow>
                <SubRoutesFormField
                    ref={subRoutesRef}
                    prefix={"ride.route"}
                    onRowAdd={async () => {
                        updateHiddenFields();
                    }}
                    onRowRemove={async () => {
                        updateHiddenFields();
                        await fetchDistance();
                    }}
                    onRowChange={async () => {
                        updateHiddenFields();
                        await fetchDistance();
                    }}
                    disabled={!!selectedRoute}
                />
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.destinationPlace')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller
                            name={'ride.route.destinationPlace'}
                            rules={{required: true}}
                            control={control}
                            defaultValue={''}
                            render={({onChange}) => (
                                <CityAutocomplete disabled={!!selectedRoute}
                                                  defaultValue={destinationPlace}
                                                  setCountry={setDestinationPlaceCountryCallback}
                                                  setCoordinates={handleDestinationCoordinatesSet}
                                                  onChangeCallback={async (place: any) => {
                                                      onChange(place);
                                                      updateHiddenFields();
                                                      await fetchDistance();
                                                  }}
                                                  className={errors.ride?.route?.destinationPlace ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('rideRoute.destinationPlace') ? 'warning' : '')}
                                />
                            )}
                        />
                        <input ref={register}
                               name={'ride.route.destinationPlaceCountry'}
                               type={"hidden"}
                        />
                        <input ref={register}
                               name={'ride.route.destinationLatitude'}
                               type={"hidden"}
                        />
                        <input ref={register}
                               name={'ride.route.destinationLongitude'}
                               type={"hidden"}
                        />
                    </IonCol>
                </IonRow>
                <input ref={register({setValueAs: v => v === 'true'})}
                       name={'ride.route.isLocal'}
                       type={"hidden"}/>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.distance')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <StyledInputGroup unit={true} className={errors.ride?.route?.distance ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('rideRoute.distance') ? 'warning' : '')}>
                    <IonCol size="10">
                        <StyledInput
                            readOnly={rideMatrix?.isDistanceEditBlocked ?? true}
                            disabled={!!selectedRoute}
                            type="number"
                            ref={register({
                                required: true,
                                valueAsNumber: true
                            })}
                            onInput={(event) => handleDistanceChanged((event.target as HTMLInputElement).value)}
                            name={'ride.route.distance'}
                        />
                    </IonCol>
                    <IonCol size="2" className="label">
                        <StyledInputUnit>km</StyledInputUnit>
                    </IonCol>
                </StyledInputGroup>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.coordinator')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller
                            name={'ride.route.coordinator'}
                            control={control}
                            defaultValue={''}
                            rules={{
                                required: true,
                                valueAsNumber: true,
                            }}
                            render={() => (
                                <StyledInput
                                    disabled={!!selectedRoute || !startingPlace || !destinationPlace}
                                    placeholder={coordinator ? coordinator.name : t('travelAllowance.ride.selectCoordinator')}
                                    className={errors.ride?.route?.coordinator ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('rideRoute.coordinatorId') ? 'warning' : '')}
                                    onClick={() => setIsCoordinatorModalOpen(prevState => !prevState)}
                                >
                                </StyledInput>
                            )}
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.placeOfService')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller
                            name={'ride.route.placeOfService'}
                            control={control}
                            defaultValue={''}
                            rules={{
                                required: true,
                                valueAsNumber: true,
                            }}
                            render={() => (
                                <StyledInput
                                    disabled={!!selectedRoute || !coordinator}
                                    placeholder={placeOfService ? placeOfService.name : t('travelAllowance.ride.selectPlaceOfService')}
                                    className={errors.ride?.route?.placeOfService ? 'error' : (rideFormContext.rideChanges?.hasOwnProperty('rideRoute.placeOfServiceId') ? 'warning' : '')}
                                    onClick={() => setIsPlaceOfServiceModalOpen(prevState => !prevState)}
                                >
                                </StyledInput>
                            )}
                        />
                    </IonCol>
                </IonRow>
                {
                    rideFormContext.type === RidePaneType.ADD &&
                    <IonRow>
                        <IonItem lines="none" style={{width: "100%"}}>
                            <StyledIonLabel className="ion-text-wrap">{t('travelAllowance.ride.saveRoute')}</StyledIonLabel>
                            <Controller name={'ride.saveAsRoute'}
                                        control={control}
                                        defaultValue={true}
                                        render={({value, onChange}) => (
                                            <StyledIonCheckbox
                                                style={{margin: "0 9px"}}
                                                disabled={!!selectedRoute}
                                                checked={selectedRoute ? false : value}
                                                slot="start"
                                                onIonChange={(e) => onChange(e.detail.checked)}
                                            />
                                        )}
                            />
                        </IonItem>
                    </IonRow>
                }
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.car')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="12">
                        <Controller name={'ride.car'}
                                    control={control}
                                    rules={{required: true}}
                                    defaultValue={control.getValues()?.ride?.car || ''}
                                    render={({onChange}) => (
                                        <StyledIonSelect
                                            placeholder={t('travelAllowance.ride.selectCar')}
                                            onIonChange={(e) => {
                                                onChange(e.detail.value);
                                                setShouldFetchRideCost(true);
                                            }}
                                            value={control.getValues()?.ride?.car || ''}
                                            className={errors.ride?.car ? 'error' : ''}
                                        >
                                            {rideFormContext.cars.map((car: Car) => {
                                                return <IonSelectOption
                                                    key={car.id}
                                                    value={car.id}>{car.brand} {car.model} {car.licensePlate}</IonSelectOption>
                                            })}
                                        </StyledIonSelect>
                                    )}
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.rate')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <StyledInputGroup unit={true}>
                    <IonCol size="10" className="label">
                        <StyledInput value={rideCost?.rate || '-'} disabled={true}/>
                    </IonCol>
                    <IonCol size="2" className="label">
                        <StyledInputUnit>{rideCost?.currency || '-'}/km</StyledInputUnit>
                    </IonCol>
                </StyledInputGroup>
                <IonRow className="mt-8">
                    <IonCol size="12">
                        <IonLabel>
                            {t('travelAllowance.ride.cost')}
                        </IonLabel>
                    </IonCol>
                </IonRow>
                <StyledInputGroup unit={true}>
                    <IonCol size="10" className="label">
                        <StyledInput value={rideCost?.amount || '-'} disabled={true}/>
                    </IonCol>
                    <IonCol size="2" className="label">
                        <StyledInputUnit>{rideCost?.currency || '-'}</StyledInputUnit>
                    </IonCol>
                </StyledInputGroup>
                <RideCostInfoRow rideRouteDistance={distanceValue}
                                 rideCostDistance={!shouldFetchRideCost ? rideCost?.distance : undefined}
                                 rideCostAmount={!shouldFetchRideCost ? rideCost?.amount : undefined}
                />
            </Accordion>
            <Accordion isOpen={true} allowToggle={true} title={t('travelAllowance.ride.passengers')}>
                <PassengersFormField prefix={'ride'}
                                     onChange={(passengers) => {
                                         setSelectedPassengers(passengers);
                                         setShouldFetchRideCost(true);
                                     }}
                                     defaultPassengers={defaultPassengers}
                                     coordinator={coordinator}
                                     placeOfService={placeOfService}
                                     date={dateStart}
                />
            </Accordion>
        </>
    );
};

export default RideFormFields;
